import type { ConversationMessagesModel } from "@certa/queries/queries/comms/getConversationMessages.query";
import type { AddCommentResponse } from "@certa/queries/types/comments.types";
import type { UserData } from "@certa/common/utils/userStorage";

export type OptimisticId = `optimistic-${number}` | (string & {});

export type OptimisticConversationMessagesModel = Omit<
  ConversationMessagesModel,
  "kryptonId"
> & { kryptonId: OptimisticId };

/**
 * Creates an optimistic message for immediate UI feedback
 */
export const createOptimisticMessage = ({
  messageContent,
  conversationId,
  mentionedUsers,
  mentionedGroups,
  currentUser
}: {
  messageContent: string;
  conversationId: string;
  mentionedUsers: number[];
  mentionedGroups: number[];
  currentUser: Partial<UserData>;
}): OptimisticConversationMessagesModel => {
  const optimisticId = `optimistic-${Date.now()}`;

  return {
    kryptonId: optimisticId,
    conversationId,
    body: messageContent,
    postedBy: {
      id: currentUser?.id || 0,
      firstName: currentUser?.first_name || "",
      lastName: currentUser?.last_name || "",
      email: currentUser?.email || "",
      fullName:
        currentUser?.first_name && currentUser?.last_name
          ? `${currentUser.first_name} ${currentUser.last_name}`
          : currentUser?.email || "You",
      groups: currentUser?.groups?.map(g => g.id) || []
    },
    postedAt: new Date().toISOString(),
    mentionedGroups,
    mentionedUsers,
    attachments: []
  };
};

/**
 * Parses server response to extract the real message for optimistic replacement
 */
const parseServerResponseForMessage = ({
  data,
  conversationId,
  currentUser
}: {
  data: AddCommentResponse;
  conversationId: string;
  currentUser: Partial<UserData>;
}) => {
  let realMessage = null;

  // Try to find the message in the response structure
  if (data.results && Array.isArray(data.results)) {
    // Look for the newest message in results
    const channels = data.results;
    for (const channel of channels) {
      if (channel.threads) {
        for (const [, thread] of Object.entries(channel.threads)) {
          if (thread.messages && Array.isArray(thread.messages)) {
            // Get the last message (newest)
            const lastMessage = thread.messages[thread.messages.length - 1];
            if (lastMessage) {
              realMessage = {
                kryptonId: lastMessage.id,
                conversationId,
                body: lastMessage.message,
                postedBy: {
                  id: lastMessage.posted_by?.id || currentUser?.id || 0,
                  firstName:
                    lastMessage.posted_by?.first_name ||
                    currentUser?.first_name ||
                    "",
                  lastName:
                    lastMessage.posted_by?.last_name ||
                    currentUser?.last_name ||
                    "",
                  email:
                    lastMessage.posted_by?.email || currentUser?.email || "",
                  fullName:
                    currentUser?.first_name && currentUser?.last_name
                      ? `${currentUser.first_name} ${currentUser.last_name}`
                      : currentUser?.email || "You",
                  groups:
                    lastMessage.posted_by?.groups?.map(g => g.id) ||
                    currentUser?.groups?.map(g => g.id) ||
                    []
                },
                postedAt: lastMessage.created_at || new Date().toISOString(),
                mentionedGroups: lastMessage.mentioned_groups || [],
                mentionedUsers: lastMessage.mentioned_users || [],
                attachments: lastMessage.attachments || []
              };
              break;
            }
          }
        }
      }
      if (realMessage) break;
    }
  }
  return realMessage;
};

/**
 * Handles optimistic message replacement logic
 */
export const handleOptimisticMessageReplacement = ({
  optimisticMessageId,
  serverResponse,
  conversationId,
  currentUser,
  replaceOptimisticMessage,
  removeOptimisticMessage
}: {
  optimisticMessageId: string;
  serverResponse: AddCommentResponse;
  conversationId: string;
  currentUser: Partial<UserData>;
  replaceOptimisticMessage: (
    id: string,
    message: ConversationMessagesModel
  ) => void;
  removeOptimisticMessage: (id: string) => void;
}): void => {
  try {
    const realMessage = parseServerResponseForMessage({
      data: serverResponse,
      conversationId,
      currentUser
    });

    if (realMessage) {
      replaceOptimisticMessage(optimisticMessageId, realMessage);
    } else {
      // Fallback: remove optimistic message, cache invalidation will show real message
      removeOptimisticMessage(optimisticMessageId);
    }
  } catch (error) {
    console.warn("Failed to replace optimistic message:", error);
    // Fallback: remove optimistic message
    removeOptimisticMessage(optimisticMessageId);
  }
};
